import { Box, Typography, List, ListItem } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatSource } from '../../api/data-contracts';
import { viewOriginalDocument, downloadOriginalDocument } from '../../utils/documents/documentHelpers';
import { isUserUploadedDocument } from '../../utils/documents/documentTypeUtils';
import { FAQDetailDialog } from '../faqs/FAQDetailDialog';
import { useAuth } from '../../hooks/auth/useAuth';
import { log } from '../../utils/errors/logger';

interface SourcesProps {
  sources: ChatSource[];
}

export const Sources: React.FC<SourcesProps> = ({ sources }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [selectedFaqId, setSelectedFaqId] = useState<string | null>(null);

  if (!sources || sources.length === 0) {
    return null;
  }

  // Function to correct SAP Discovery Center URLs
  const getCorrectUrl = (url: string) => {
    if (!url) {return '';}
    
    // Check if this is a Discovery Center URL with a UUID
    if (url.includes('discovery-center.cloud.sap/serviceCatalog/')) {
      // Extract the UUID from the URL
      const uuidMatch = url.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
      if (uuidMatch && uuidMatch[1]) {
        // Return the correctly formatted URL
        return `https://discovery-center.cloud.sap/protected/index.html#/ai-feature/${uuidMatch[1]}/`;
      }
    }
    
    // Return the original URL if it's not a Discovery Center URL or doesn't contain a UUID
    return url;
  };

  // Handle opening a document
  const handleDocumentClick = (source: ChatSource) => {
    log.debug('Source clicked', {
      title: source.title,
      source_type: source.source_type,
      document_id: source.document_id,
      url: source.url,
      hasDocId: !!source.document_id
    });

    // Handle various source formats from different places in the code
    if (source.source_type === 'faq' && source.document_id) {
      log.user.action('faq_opened', 'Sources', {
        faq_id: source.document_id,
        faq_title: source.title
      });
      // Open FAQ detail dialog
      setSelectedFaqId(source.document_id);
      setFaqDialogOpen(true);
    } else if (isUserUploadedDocument(source.source_type) && source.document_id) {
      log.user.action('document_viewed', 'Sources', {
        document_id: source.document_id,
        document_type: source.source_type,
        document_title: source.title
      });
      // Direct document ID
      void viewOriginalDocument(source.document_id);
    } else if (source.url?.startsWith('/document/view/')) {
      log.user.action('document_viewed', 'Sources', {
        url: source.url,
        document_title: source.title
      });
      // URL from chat history
      void viewOriginalDocument(source.url);
    } else if (source.url) {
      log.user.action('external_link_opened', 'Sources', {
        url: source.url,
        source_title: source.title
      });
      // External URL
      window.open(getCorrectUrl(source.url), '_blank');
    } else {
      log.warn('Source click handler - no action matched', {
        source_type: source.source_type,
        has_document_id: !!source.document_id,
        has_url: !!source.url
      });
    }
  };

  // Deduplicate sources by title and URL/document_id
  const uniqueSources: ChatSource[] = [];
  const seenKeys = new Set();
  
  sources.forEach(source => {
    // Create a unique key for each source based on document_id or url
    let key;
    if (source.document_id) {
      key = `doc:${source.document_id}`;
    } else if (source.url) {
      // For URLs that are document paths, extract the document ID
      if (source.url.startsWith('/document/view/')) {
        key = `doc:${source.url.replace('/document/view/', '')}`;
      } else {
        key = `url:${source.url}`;
      }
    } else {
      // Fallback to title if no other identifier is available
      key = `title:${source.title}`;
    }
    
    // Only add if we haven't seen this source before
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      uniqueSources.push(source);
    }
  });

  return (
    <Box sx={{ mt: 1 }}>
      <Typography 
        variant="subtitle2" 
        sx={{ 
          color: '#666666',
          fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
          fontWeight: 500 
        }}
      >
        {t('chat.sources.title')}
      </Typography>
      <List dense>
        {uniqueSources.map((source, index) => (
          <ListItem key={index} sx={{ py: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666666',
                  fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
                  marginRight: 1
                }}
              >
                {source.title}
              </Typography>
              
              {/* Relevance score badge */}
              {source.relevance_score && source.relevance_score > 0 && (
                <Box 
                  sx={{ 
                    backgroundColor: source.relevance_score >= 0.8 ? '#4caf50' : 
                                     source.relevance_score >= 0.6 ? '#ff9800' : '#f44336',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '12px',
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    marginRight: 1,
                    minWidth: '35px',
                    textAlign: 'center'
                  }}
                >
                  {Math.round(source.relevance_score * 100)}%
                </Box>
              )}
              
              {/* View option */}
              <button 
                onClick={() => handleDocumentClick(source)} 
                style={{ 
                  marginRight: 8,
                  color: '#003064',
                  textDecoration: 'none',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  background: 'none',
                  border: 'none',
                  padding: 0
                }}
              >
                {t('chat.sources.actions.view')}
              </button>
              
              {/* Download option - only for custom documents */}
              {(source.source_type === 'custom' || (source.url?.startsWith('/document/'))) && (
                <button
                  onClick={() => {
                    // Get document ID based on available properties
                    let docId = source.document_id;

                    // If URL starts with /document/view, convert to download URL
                    if (source.url?.startsWith('/document/view/')) {
                      docId = source.url.replace('/document/view/', '');
                    }

                    // If URL already has download path, use as is
                    if (source.download_url) {
                      downloadOriginalDocument(source.download_url);
                    } else if (docId) {
                      downloadOriginalDocument(docId);
                    }
                  }}
                  style={{ 
                    color: '#003064',
                    textDecoration: 'none',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    background: 'none',
                    border: 'none',
                    padding: 0
                  }}
                >
                  {t('chat.sources.actions.download')}
                </button>
              )}
            </Box>
          </ListItem>
        ))}
      </List>

      {/* FAQ Detail Dialog */}
      <FAQDetailDialog
        open={faqDialogOpen}
        onClose={() => {
          setFaqDialogOpen(false);
          setSelectedFaqId(null);
        }}
        faqId={selectedFaqId}
        isModerator={user?.is_moderator || false}
        currentUserId={user?.user_id || null}
      />
    </Box>
  );
};